import { useEffect, useRef, useState } from "react"
import type { Media } from "@/shared/lib/types"

interface UsePostCardMediaOptions {
	media?: Media[]
	title?: string | null
}

export function usePostCardMedia({ media }: UsePostCardMediaOptions) {
	const [isMediaLoaded, setIsMediaLoaded] = useState(false)
	const [hasMediaError, setHasMediaError] = useState(false)
	const [previewSrc, setPreviewSrc] = useState<string | null>(null)
	const [videoSrc, setVideoSrc] = useState<string | null>(null)
	const [videoPosterSrc, setVideoPosterSrc] = useState<string | null>(null)
	const [isVideoPosterLoaded, setIsVideoPosterLoaded] = useState(false)
	const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
	const [isVideoReady, setIsVideoReady] = useState(false)
	const mediaLoadedRef = useRef(false)
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const mediaContainerRef = useRef<HTMLDivElement | null>(null)
	const [videoIntersectionRatio, setVideoIntersectionRatio] = useState(0)
	const [isMuted, setIsMuted] = useState(true)

	useEffect(() => {
		setHasMediaError(false)
		setIsMediaLoaded(false)
		mediaLoadedRef.current = false
		setPreviewSrc(null)
		setVideoSrc(null)
		setVideoPosterSrc(null)
		setIsVideoPosterLoaded(false)
		setShouldLoadVideo(false)
		setIsVideoReady(false)
		setVideoIntersectionRatio(0)
		setIsMuted(true)

		const videoMedia = media?.find((m) => m.type === "video" || m.type === "video_note")
		const posterMedia =
			media?.find((m) => m.type === "preview") || media?.find((m) => m.type === "photo")

		if (videoMedia) {
			setVideoSrc(videoMedia.url)
			setVideoPosterSrc(posterMedia?.url || videoMedia.thumbnailUrl || null)
		} else if (posterMedia) {
			setPreviewSrc(posterMedia.url)
		} else {
			return
		}

		const timeout = window.setTimeout(() => {
			if (!mediaLoadedRef.current) {
				setHasMediaError(true)
			}
		}, 10000)

		return () => window.clearTimeout(timeout)
	}, [media])

	useEffect(() => {
		if (!videoSrc || !mediaContainerRef.current) return

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				setVideoIntersectionRatio(entry?.intersectionRatio ?? 0)
			},
			{ threshold: [0, 0.25, 0.5, 0.75, 0.85, 0.9, 1] },
		)

		observer.observe(mediaContainerRef.current)
		return () => observer.disconnect()
	}, [videoSrc])

	useEffect(() => {
		if (!videoSrc) return
		const shouldPlay = videoIntersectionRatio >= 0.85
		if (!shouldPlay) {
			setShouldLoadVideo(false)
			setIsVideoReady(false)
			const el = videoRef.current
			if (el) {
				el.pause()
				try {
					el.currentTime = 0
				} catch {}
			}
			return
		}

		const timeout = window.setTimeout(() => setShouldLoadVideo(true), 250)
		return () => window.clearTimeout(timeout)
	}, [videoSrc, videoIntersectionRatio])

	useEffect(() => {
		if (!videoSrc || !shouldLoadVideo || videoIntersectionRatio < 0.85) return

		const el = videoRef.current
		if (!el) return
		el.muted = isMuted
		el.playsInline = true
		const p = el.play()
		if (p) p.catch(() => undefined)
	}, [videoSrc, shouldLoadVideo, videoIntersectionRatio, isMuted])

	const toggleMute = () => {
		const nextMuted = !isMuted
		setIsMuted(nextMuted)
		if (!nextMuted) setShouldLoadVideo(true)

		const el = videoRef.current
		if (!el) return
		el.muted = nextMuted
		if (!nextMuted) {
			const p = el.play()
			if (p) p.catch(() => undefined)
		}
	}

	const hasMediaAttempt = Boolean(videoSrc || previewSrc)
	const hasMedia = hasMediaAttempt && !hasMediaError

	return {
		hasMedia,
		hasMediaAttempt,
		hasMediaError,
		previewSrc,
		videoSrc,
		videoPosterSrc,
		isMediaLoaded,
		isVideoPosterLoaded,
		isVideoReady,
		shouldLoadVideo,
		isMuted,
		videoIntersectionRatio,
		mediaContainerRef,
		videoRef,
		mediaLoadedRef,
		setIsMediaLoaded,
		setIsVideoPosterLoaded,
		setIsVideoReady,
		setHasMediaError,
		setShouldLoadVideo,
		toggleMute,
	}
}
